import {
    isNaN,
    isNumber,
    isString,
    isObject,
    isArray,
    isFunction,
    deepClone,
    deepMixin,
    removeRepeat,
    parseDate,
    clientTime2serverTime,
    currencyToNumber,
    round2,
    toUpperFields,
    getId,
    setId,
    shortestString
} from "../../funcs";
import $ from "jquery";
import Events from "../Events";
import langDictionary from "../lang/init";
import RowsCollection from "./RowsCollection";
import Columns from "./Columns";
import TableApi from "../api/TableApi";
import Filter from "../api/filter/Filter";
import Filter3 from "./filter3/Filter3";
import config from "./config";
import dictionaries from "json!/api/entities/dictionaries";
import scheme from "json!/api/entities/scheme";


let colId = 0;
scheme = scheme.result;
delete scheme.status;

dictionaries = dictionaries.result;
delete dictionaries.status;

const deadTables = {};
for (const entityName in dictionaries) {
    const entitySchema = scheme[ entityName ];
    deadTables[ entitySchema.realTable ] = entityName;
}

/**
 * @typedef Column
 * @type {object}
 * @property {string} key - ключ столбца
 * @property {string} type - тип данных (как в entity --type)
 * @property {title} [title] - название столбца (для пользователя)
 * @property {object} [info] - доп. настройки для колонки
 */
/**
 * @typedef Filter2Condition
 * @type {object}
 * @property {string} key - Столбец, на который накладываем условие фильтрации
 * @property {true} [not] - инверсия условия
 * @property {any} [value] - значения, для подстановки в условие (see type)
 * @property {"equal" | "contain" | "in" | "date" | "composite"} type - тип фильтрации:
 *
 * ```js
 * // "equal" сравнение
 * {
 * type: "equal",
 * key: "ID_COUNTRY",
 * value: 1
 * }
 * ```
 *
 * ```js
 * // "contain" поиск подстроки
 * {
 * type: "contain",
 * key: "NAME",
 * value: "подстрока"
 * }
 * ```
 *
 * ```js
 * // "in" вхождение в массив
 * {
 * type: "in",
 * key: "ID_COUNTRY",
 * value: [1, 2]
 * }
 * ```
 *
 * ```js
 * // "date" вхождение в один из интервалов дат
 * {
 * type: "date",
 * key: "DT_CREATE",
 * intervals: [{ start: Date, end: Date }]
 * }
 * ```
 *
 * ```js
 * // "composite" проверка на "is null"
 * {
 * type: "composite"
 * key: "ID_COUNTRY",
 * onlyNulls: true
 * }
 * ```
 * ```js
 * // "composite" проверка на "is NOT null"
 * {
 * type: "composite"
 * key: "ID_COUNTRY",
 * withNulls: false
 * }
 * ```
 * @property {true} [onlyNulls] - is null
 * @property {false} [withNulls] - is not null
 * @property {Array<{start: Date | string, end: Date | string}>} [intervals] - только для типа "date", интервалы дат
 */
/**
 * @typedef Filter2
 * @type {{[condition: string]: Filter2Condition}}
 */
/**
 * @typedef GetParams
 * @type {object}
 * @property {string[]} [columns] - какие поля должны содержать модели в ответе
 * @property {number} [offset] - сколько строк нужно пропустить в начале
 * @property {number | "all"} [limit] - сколько строк нужно вернуть
 * @property {Filter2 | Filter} [filter] - отсев данных, фильтруем условия по AND
 * @property {Filter3} [filter3] - отсев данных, фильтруем условия по AND и OR
 *
 * ```js
 * await storage.get({
 * filter: {
 *     isRus: {
 *         key: "ID_COUNTRY",
 *         type: "equal",
 *         value: 1
 *     },
 *     // and
 *     hasName: {
 *       key: "NAME",
 *       type: "composite",
 *       withNulls: false
 *   }
 * }
 * })
 * ```
 *
 * @property {string} [search] - поиск подстроки в любом из столбцов
 * @property {object} [scope] - переменные для подстановки в запрос,
 * в entity должны быть указаны типы для переменных
 * @property {object[]} [sort] - сортировка по столбцам [{COLUMN: 1 or -1}]
 *
 * ```js
 * // ID по возрастанию
 * await storage.get({
 *    sort: [{ ID: 1 }]
 * })
 * ```
 *
 * ```js
 * // ID по убыванию
 * await storage.get({
 *    sort: [{ ID: -1 }]
 * })
 * ```
 *
 * ```js
 * // ID_COUNTRY по убыванию, но если есть повторы,
 * // то сортируем по ID по возрастанию
 * await storage.get({
 *    sort: [{ ID_COUNTRY: -1 }, {ID: 1}]
 * })
 * ```
 */

// @see it in system.js
// RowsStorage.EXCLUDE_FROM_RIGHTS_FILTER = RightsManager.BaseRights.EXCLUDE_FROM_RIGHTS_FILTER

/**
 * RowsStorage это Умное хранилище данных, умеющее самостоятельно фильтровать и
 * сортировать маленькие таблицы.
 *
 * - RowsStorage должен обладать тем же интерфейсом, что и TableApi
 * - Напрямую создавать объект RowsStorage не нужно
 *  вместо этого используем статичный метод RowsStorage.get
 *  RowsStorage.get("LIST_COMPANY")
 * - id моделей храниться в поле row.id у каждой модели строки
 *
 *  rowsStorage.collection @see RowsCollection.js
 *
 * @class RowsStorage
 * @extends Events
 * @param {object} params объект вида
 * {
 *  name: string - имя таблицы
 * 	size: number - базовый размер буфера
 * }
 *
 */

let storages = {};
let id = 0;
let localStorageId = 0;

const allStoragesEvents = new Events();

export default class RowsStorage extends Events {

    static getShortestEntityNameByTable(table) {
        const entitiesNames = [];
        for (const [entityName, entityConfig] of Object.entries(scheme)) {
            if ( entityConfig.realTable == table ) {
                entitiesNames.push(entityName);
            }
        }

        return shortestString(entitiesNames);
    }

    static getTableName(entityName) {
        return scheme[ entityName ].realTable;
    }

    constructor(params) {
        super();

        if ( isString(params) ) {
            params = { name: params };
        }
        params = deepMixin({}, params);
        // имя таблицы
        this.name = params.name;
        this.params = params;

        // Словарь из Ид строк которые были перемещены из корзины
        // при применении метода untrash
        // На клиенте
        this._untrashed = null;

        // если параллельно отправить два одинаковых запроса,
        // то на сервер уйдет только один
        this._nowRunning = {
            get: {},
            getCount: {},
            indexOf: {},
            export: {}
        };

        // в качестве хранилища всех строк используем Backbone.Collection
        this.initCollection();

        // api - объект, с которым мы будем загружать строки
        this.api = new TableApi(this.name);
        this.api.processRow = this._processRow.bind(this); // TAG: currency10000

        // чтобы извне не подключать фильтр
        this.Filter = function(query, conds) {
            return new Filter(query, conds);
        };

        this.reInit();


        // для удобства использования
        // создаем аналог .bind
        // только с текущим контекстом
        // obj.method.bind(obj, a, b)
        // =>
        // obj.method_with(a, b)
        //
        // P.S. положительные эффекты:
        // кровоточие глаз -1
        //
        // P.P.S. пример использования:
        //   var storage = RowsStorage.get("LIST_COMPANY");
        //   var rows = [{}, {}];
        //
        //   f.series(rows.map(function(row) {
        //      return storage.create_with(row);
        //   }), function(err, models) {
        //      console.log(models);
        //   });
        //
        // P.P.P.S.
        // Такое не прокатит:
        // f.parallel( [1,2].map(storage.get_with) )
        // потому что, у .map 3 аргумента
        //
        const keys = [
            "get",
            "update",
            "create",
            "trash",
            "untrash",
            "remove"
        ];

        let that = this;
        keys.forEach((key) => {
            let method = this[ key ];

            this[ key + "_with" ] = function() {
                let args = [].slice.call(arguments);
                return function() {
                    let args2 = [].slice.call(arguments);
                    return method.apply(that, args.concat(args2));
                };
            };
        });

        this.initSocket();
        this.initLangChanges();
    }

    static onReadySocket(socketClient) {
        socketClient.on("entity:change", (event) => {
            onChangeEntitySchema(event.name, event.json, event.rows);
        });
        socketClient.on("dead-rows:change", (event) => {
            onChangeDeadRows(event.entity, event.rows);
        });

        function onChangeEntitySchema(entityName, entitySchema, rows) {
            // set new schema for reInit
            scheme[ entityName ] = entitySchema;

            // entity can change DeadStatus (@keep_in_memory)
            delete deadTables[ entitySchema.realTable ];

            // if entity is "dead" (@keep_in_memory)
            if ( Array.isArray(rows) ) {
                dictionaries[ entityName ] = rows;
                deadTables[ entitySchema.realTable ] = entityName;
            }

            const storageKey = entityName + ".DELETED:0";
            const storage = storages[ storageKey ];
            if ( storage ) {
                storage.reInit();
            }
        }

        function onChangeDeadRows(entityName, newRows) {
            dictionaries[ entityName ] = newRows;

            const storageKey = entityName + ".DELETED:0";
            const storage = storages[ storageKey ];
            if ( storage ) {
                storage.reInit();
            }
        }
    }

    /**
     * Получить единственный экземпляр класса RowsStorage,
     * для каждой таблицы, один экземпляр RowsStorage
     * @param  {String} name имя таблицы
     * @param {0|1} [DELETED]
     * @return {RowsStorage} коллекция
     */
    static get(name, DELETED) {
        DELETED = DELETED || 0;
        let storageKey = name + ".DELETED:" + DELETED;
        let storage = storages[storageKey];

        if ( !storage ) {
            storage = new RowsStorage({
                name,
                DELETED
            });
            storages[storageKey] = storage;

            if ( config[name] && config[name].plugins ) {
                config[name].plugins.map(function(plugin) {
                    window.require(["classes/storage/plugins/" + plugin.name], function(Plugin) {
                        new Plugin(storage, plugin.params);
                    });
                });
            }
        }

        return storage;
    }

    reInit() {
        this.hasScope = false;

        const tableScheme = scheme[ this.name ];
        if ( tableScheme ) {
            for (const key in tableScheme.columns) {
                const column = tableScheme.columns[key];

                column.id = ++colId;
                column.title = langDictionary.getFieldTitle(this.name, column.key);

                if ( column.default === "$user" ) {
                    column.default = extractUserId();
                }
            }

            // переменные в entity, @see FIN_OPERATION_BY_UNIT
            this.hasScope = (
                tableScheme.scope
                && Object.keys(tableScheme.scope).length > 0
            );
        }


        // все поля таблицы
        this.columns = this.columns || new Columns();
        this.columns.reset();

        if ( tableScheme ) {
            if ( tableScheme.columns ) {
                this.columns.add(tableScheme.columns);
            }

            if ( tableScheme.mainField ) {
                this.mainField = tableScheme.mainField;
            }

            if ( tableScheme.realTable ) {
                this.realName = tableScheme.realTable.toUpperCase();
            }

            if ( tableScheme.joinTables ) {
                this.joinTables = tableScheme.joinTables;
            }

            if ( tableScheme.unique ) {
                this.unique = tableScheme.unique;
            }
        }

        this.initUpdateLinkTables();

        const deadRows = dictionaries[ this.name ];
        if ( isArray(deadRows) ) {
            this.collection.reset();

            deadRows.map((row) => {
                this._addRowToCache(row);
            });
            // @see GridList
            this.keep_in_memory = true;
        }

        // for grids and forms
        this.trigger("entity:change");
    }

    isLocal() {
        return this._isLocal;
    }

    getRightsAccessFilter(filter) {
        if ( this._isLocal ) {
            return filter;
        }
        if ( this.name in RowsStorage.EXCLUDE_FROM_RIGHTS_FILTER ) {
            return filter;
        }

        // RowsStorage используется чтобы вытянуть настройки, данные пользователя и тд
        if ( system.rights ) {
            if ( !filter ) {
                filter = new Filter();
            }
            if ( !(filter instanceof Filter ) ) {
                filter = new Filter(filter);
            }

            let rights = system.rights.getBaseRights();
            let filterRights = rights.getTableFilter("view", this.name);

            filter = new Filter3(filter, true);
            filter = filter.and(filterRights);
        }

        return filter;
    }

    _processRow(row) {
        for (let key in row) {
            let column = this.columns.getByKey(key);

            if (
                column
                && column.get("type") == "currency"
            ) {
                let value = row[ key ];
                if ( value == null || value === "" ) {
                    value = null;
                } else {
                    value = value / 10000; // TAG: currency10000
                }
                row[key] = value;
            }
        }
        return row;
    }

    /**
     * Проверяет существует ли колонка, если нет прокидавает ошибку
     * @param {string} columnName
     */
    _checkColumnIsExists(columnName) {
        let isExists = this.columns.getByKey(columnName);

        if ( !isExists ) {
            // в фильтрах могут оставаться удаленные столбцы
            // а позже на основании этих параметров делается запрос
            // и если столбца нет то будет ошибка
            throw new Error(`entity column "${columnName}" is not exists in "${this.name}"`);
        }
    }

    /**
     * Подготовка входных параметров (нужно для DELETED, attach, columns "all" и прочее)
     *
     * getParams is: id or filter or { filter, sort, attach, ... }
     */
    _prepareGetParams(method, params, __noSync) {
        let tempData = {
            original: params
        };
        params = deepClone(params);

        // много раз попадался на след баг:
        // table.get(filter(..someFilter..), func...)
        // в итоге решил, что если в качестве params передается
        // только фильтр, то автопреобразую его
        if ( params instanceof Filter ) {
            params = { filter: params };
        }

        // params is id
        if ( isString(params) || isNumber(params) ) {
            params = {
                filter: {
                    loadRow: {
                        key: "ID",
                        type: "equal",
                        value: params
                    }
                },
                offset: 0,
                limit: "all",
                columns: "all"
            };

            tempData.isLoadRow = true; // !!!!!!!!!!!
        }
        // getCount without params
        else if ( !params ) {
            params = {
                offset: 0,
                limit: "all"
            };
        }

        params._tempData = tempData;

        if ( params.columns == "all" || !params.columns ) {
            params.columns = this.columns.map(function(column) {
                return column.get("key");
            });
        }

        params.DELETED = this.params.DELETED;
        let id;

        // optimize
        let excludeFromSearch = params.excludeFromSearch || [];
        if ( isString(params.search) ) {
            // чекбоксы содержат да или нет, пока просто выкидываем их
            excludeFromSearch = excludeFromSearch.concat(
                params.columns.filter(function(key) {
                    let column = this.columns.getByKey(key);
                    let type = column && column.get("type");

                    return type == "checkbox";
                }, this)
            );

            // если содержит букву, то фильтровать по числовым столбцам нет смысла
            // тоже самое с датами
            if ( !/^[\d.-:]+$/.test(params.search) ) {
                excludeFromSearch = excludeFromSearch.concat(
                    params.columns.filter(function(key) {
                        let column = this.columns.getByKey(key);
                        let type = column && column.get("type");

                        return (
                            type == "number"
                            || type == "currency"
                            || type == "date"
                            || type == "datetime"
                            || type == "minute"
                        );
                    }, this)
                );
            }
        }

        if ( isArray(params.columns) ) {
            const outputColumns = [];
            params._tempData.deadJoins = [];

            params.columns.slice().forEach((key) => {
                const column = this.columns.getByKey(key);
                if ( !column ) {
                    outputColumns.push(key);
                    return;
                }

                const type = column.get("type");
                const original = column.get("original") || {};
                const idKey = (original.id || "").toUpperCase();
                const idColumn = this.columns.getByKey(idKey);

                if ( type == "gridlist" && idColumn ) {
                    // для корректного редактирования gridlist'а
                    // нужно знать id выбранной позиции
                    if ( !params.columns.includes(idKey) ) {
                        outputColumns.push(idKey);
                        excludeFromSearch.push(idKey);
                    }

                    // убираем из запроса столбцы на мертвые справочники
                    if ( !params.search && !params.distinct && isDeadJoin(column) ) {
                        params._tempData.deadJoins.push({
                            columnKey: key,
                            entityName: getDeadJoinEntityName(column),
                            idKey,
                            mainField: column.get("realColumnName").toUpperCase()
                        });
                    }
                    else {
                        outputColumns.push(key);
                    }
                }
                else {
                    outputColumns.push(key);
                }
            });

            params.columns = outputColumns;
        }


        excludeFromSearch = removeRepeat(excludeFromSearch);

        if ( excludeFromSearch.length ) {
            params.excludeFromSearch = excludeFromSearch;
        }

        if ( !params.sort ) {
            params.sort = [{ ID: 1 }];
        }

        if ( !isObject(params.scope) ) {
            delete params.scope;
        }

        let filter = params.filter;
        if ( filter && !(filter instanceof Filter) ) {
            filter = new Filter( filter );
        }

        params.filter = this.getRightsAccessFilter(params.filter);

        // Cursor._onRemove  при удалениии записей делает проверку
        // model in cursor.filter
        // чтобы проверить была ли модель в кадре или нет
        //
        // Для того, чтобы проверка работала корректно
        // нужно чтобы в памяти были поля из фильтра
        //
        // P.S. это поведение полезно и для многих других ситуаций
        // нужно прошерстить фильтр на предмет столбцов которых уже нет,

        if ( params.filter && params.filter.conditions ) {

            for (let columnFilter in params.filter.conditions) {
                let columnName = params.filter.conditions[columnFilter].key;

                this._checkColumnIsExists(columnName);
            }
        }

        if ( params.filter3 && !(params.filter3 instanceof Filter3) ) {
            params.filter3 = new Filter3(params.filter3);
        }

        if ( params.filter3 ) {
            const filter3Columns = params.filter3.getColumns();

            filter3Columns.forEach((columnName) => {
                this._checkColumnIsExists(columnName);
            });
        }

        if ( method == "get" ) {
            if ( params.filter instanceof Filter ) {
                for (let name in params.filter.conditions) {
                    let condition = params.filter.conditions[ name ];

                    params.columns.push(condition.key);
                }
                params.columns = removeRepeat(params.columns);
            }

            if ( params.filter3 instanceof Filter3 ) {
                params.columns.push(...params.filter3.getColumns());
                params.columns = removeRepeat(params.columns);
            }
        }

        let offset = 0;
        if ( isNumber(+params.offset) ) {
            offset = +params.offset;
        }

        let limit = 999999999;
        if ( isNumber(+params.limit) ) {
            limit = +params.limit;
        }

        /**
         * закрепленные строки смещают все модели, идущие дальше
         * params.attach: {
         *    800: 0,
         *    795: {at: 100, ignoreFilter: true}
         * }
         *
         *  without attach    with attach          rowIndex (at)
         *  1                   800                 0
         *  2                   1                   1
         *  3                   2                   2
         *  ...                 ...                 ...
         *  100                 99                  99
         *  101                 795                 100
         *  102                 101                 101
         *  ...                 ...                 ...
         *  800                 799                 799
         *
         */
        if ( params.attach ) {
            let attach = {};
            let at;

            // если строка не подходит по фильтру,
            // то выкидываем ее из attach,
            // в рамках этого запроса
            for (at in params.attach) {
                let attachParams = params.attach[ at ];
                let ignoreFilter = attachParams.ignoreFilter || false;

                id = attachParams.id;
                let model = this.collection.get(id);

                if ( isNaN(+at) ) {
                    throw new Error("invalid attach");
                }
                at = +at;

                if (
                    model // model могли удалить
                    && ignoreFilter === false
                    && filter
                    && !filter.check(model)
                ) {
                    continue;
                }

                if (
                    model // model могли удалить
                    && ignoreFilter === false
                    && params.filter3
                    && !params.filter3.check(model)
                ) {
                    continue;
                }

                // attach моделей в конец (после запроса)
                // не влияет на ответ
                if ( at > offset + limit ) {
                    continue;
                }

                if ( at < offset ) {
                    offset--; // смещение
                    continue;
                }

                attach[ at ] = id;
            }
            params.offset = offset;

            // прикрепленные строки смещают запрос
            if ( Object.keys(attach).length ) {
                params._tempData.attach = attach;
            }
        }

        params._tempData.canSyncGet = !__noSync && this.canSyncGet(params);

        if ( this._isLocal ) {
            params._tempData.canSyncGet = true;
        }

        let key = JSON.stringify(tempData.original);
        let requests = this._nowRunning[ method ];

        tempData.key = key;

        if ( // синхронные запросы не нуждаются в nowRunning
            !params._tempData.canSyncGet
            && key in requests
        ) {
            tempData.hasParallel = true;
        }

        return params;
    }

    _prepareResponse(preparedParams, rows) {

        // в _prepareGetParams
        // убрали столбцы на мертвые справочники,
        // возвращаем их назад
        if ( preparedParams._tempData.deadJoins ) {
            for (const deadJoin of preparedParams._tempData.deadJoins) {
                const {columnKey, entityName, idKey, mainField} = deadJoin;
                const deadStorage = RowsStorage.get(entityName);

                for (const row of rows) {
                    const deadRowId = row[ idKey ];
                    const deadModel = deadStorage.collection.get(deadRowId);
                    const deadValue = deadModel && deadModel.get(mainField);

                    row[ columnKey ] = deadValue;
                }
            }
        }

        // save rows in memory
        this.___pauseUpdate = true;
        this.collection.add(rows, { merge: true });
        this.___pauseUpdate = false;

        let models = this.collection.getModels(rows);

        if ( preparedParams._tempData.isLoadRow ) {
            return models[ 0 ];
        }

        let limit = models.length;
        if ( isNumber(+preparedParams.limit) ) {
            limit = +preparedParams.limit;
        }

        let attach = preparedParams._tempData.attach;
        if ( attach ) {
            for (let at in attach) {
                let id = attach[ at ];
                let model = this.collection.get(id);

                at -= preparedParams._tempData.original.offset;

                // удаляем со старого места
                for (let i = 0, n = models.length; i < n; i++) {
                    if ( models[i].id == id ) {
                        models.splice(i, 1);
                        break;
                    }
                }
                // вставляем в новое
                if ( model ) {
                    models.splice(at, 0, model);
                }
            }
        }

        models = models.slice(0, limit);
        // отфильтровываем удаленные модели
        models = models.filter(function(model) {
            return !!model;
        });

        return models;
    }

    _loadSync(params, callback) {
        let err = null;
        let models = [];

        try {
            models = this._getSync(params);

            models = models.map(function(model) {
                return model.attributes;
            });
        } catch (e) {
            err = e;
        }

        if ( callback ) {
            return callback(err, models);
        } else if ( err ) {
            throw err;
        } else {
            return models;
        }
    }

    /**
     * подгрузить часть строк по фильтрам,
     * или загрузить одну строку по ее id
     * @param {GetParams | Number} params параметры для this.api.get
     * либо id модели, либо параметры для поиска моделей
     * @param {Function} [callback]
     * @param {Boolean} [__noSync]
     * @returns {Promise<Backbone.Model[] | Backbone.Model>}
     */
    async get(params, callback, __noSync) {

        params = this._prepareGetParams("get", params, __noSync);

        // from parallel request
        if ( params._tempData.hasParallel ) {
            let response;
            let err;
            try {
                response = await this._waitParallel("get", params);
            } catch (_err) {
                err = _err;
            }

            if ( callback ) {
                return callback(err, response);
            }

            if ( err ) {
                throw err;
            }

            return response;
        }

        let response;
        let err;

        try {
            // from collection
            if ( params._tempData.canSyncGet ) {
                response = this._loadSync(params);
            }
            // from server
            else {
                this._addRequest("get", params);
                response = await this.api.get(params);
            }
        } catch (_err) {
            err = _err;
        }

        if ( err ) {
            if ( callback ) {
                callback(err, []);
            } else {
                throw err;
            }
        }

        response = this._prepareResponse(params, response);

        if ( callback ) {
            // LW-3410  вызов callback может вызвать ошибку
            // а если не будет запущен _runParallels,
            // то какой либо запрос может подвиснуть
            // поэтому нужно обязательно использовать try/catch

            try {
                callback(err, response);
            } catch (err) {
                console.error(err);
            }
        }

        // чтобы не нарушать порядок
        // они должны быть вызваны, после return response
        setTimeout(() => {
            this._runParallels("get", params, err, response);
        });

        return response;
    }

    async _waitParallel(method, params, callback) {
        let key = params._tempData.key;
        let requests = this._nowRunning[ method ];
        let stack = requests[ key ];

        if ( !stack ) {
            console.error("invalid case");
            return;
        }

        if ( callback ) {
            stack.callbacks.push((err, response) => {
                if ( callback ) {
                    callback(err, response);
                }
            });
        } else {
            return new Promise((resolve, reject) => {
                stack.callbacks.push((err, response) => {
                    if ( err ) {
                        reject(err);
                    } else {
                        resolve(response);
                    }
                });
            });
        }
    }

    _addRequest(method, params) {
        let requests = this._nowRunning[ method ];
        let key = params._tempData.key;

        requests[ key ] = {
            callbacks: []
        };
    }

    _runParallels(method, params, err, response) {
        let key = params._tempData.key;
        let requests = this._nowRunning[ method ];
        let stack = requests[ key ];

        if ( !stack ) {
            return;
        }

        delete requests[ key ];
        stack.callbacks.forEach(function(callback) {
        // LW-3410
        // если не отлавливать ошибки в callback
        // то формы зависают
            try {
                callback(err, deepClone(response));
            } catch (err) {
                console.error(err);
            }
        });
    }

    /**
     * Если все строки, которые только есть в таблицы уже загружены в this.collection,
     * то любую фильтрацию и сортировку можно произвести на клиенте.
     * @param {GetParams | Number | String} params фильтры, сортировки и прочее или id строки
     * @return {Array | RowModel} Строки/Строка полученные в результате фильтров и сортировок
     */
    _getSync(params) {
        let id;

        if ( isObject(params) ) {
            if ( !params.sort || !params.sort.length ) {
                params = deepClone(params);
                params.sort = [{ ID: 1 }];
            }
            return this.collection.getByParams(params);
        } else {
            id = params;
            return this.collection.get(id);
        }
    }

    /**
     * Получить кол-во строк таблицы.
     * Варианты использования:
     * getCount(callback)
     * getCount({фильтры и прочее}, callback)
     * @param {GetParams} params
     * @param {Function} [callback]
     * @param {Boolean} [__noSync]
     * @returns {Promise<number>}
     */
    async getCount(params, callback, __noSync) {
        if ( isFunction(params) ) {
            callback = params;
            params = false;
        }

        if ( params && params.cache === false ) {
            __noSync = true;
        }
        if ( params && !params.filter && !params.search && !params.filter3 && !params.scope ) {
            params = false;
        }

        let count;
        let err;

        params = this._prepareGetParams("getCount", params, __noSync);
        delete params.limit;
        delete params.offset;

        if ( params._tempData.hasParallel && !this._isLocal ) {
            try {
                count = await this._waitParallel("getCount", params);
            } catch (_err) {
                err = _err;
            }
        }

        else if ( params._tempData.canSyncGet || this._isLocal ) {
            let models = this.collection.getByParams(params);
            count = models.length;
        }

        else {
            this._addRequest("getCount", params);
            try {
                count = await this.api.getCount(params);
            } catch (_err) {
                err = _err;
            }

            // чтобы не нарушать порядок
            // они должны быть вызваны после return response
            setTimeout(() => {
                this._runParallels("getCount", params, err, count);
            });
        }

        if ( callback ) {
            return callback(err, count);
        }

        if ( err ) {
            throw err;
        }

        return count;
    }

    /**
     * По фильтрам и сортировкам определить индекс строки в таблице.
     * @param  {GetParams | String | Number}   params либо id строки,
     * либо такой же набор параметров как у api.get, но с params.id
     * @returns {Promise<number>}
     */
    async indexOf(params, callback, __noSync) {
        if ( isString(params) || isNumber(params) ) {
            params = { id: params };
        }

        params = this._prepareGetParams("indexOf", params, __noSync);
        delete params.limit;
        delete params.offset;

        let index = -1;
        let err;

        if ( params._tempData.hasParallel ) {
            try {
                index = await this._waitParallel("indexOf", params);
                index = +index;
            } catch (_err) {
                err = _err;
            }
        }

        // get rows
        // все данные уже в кэше
        else if ( params._tempData.canSyncGet ) {
            let rows = [];
            try {
                rows = this._getSync(params);
                rows = rows.map((model) => model.attributes);
                rows = this._prepareResponse(params, rows);
            } catch (_err) {
                err = _err;
            }

            index = rows.findIndex((model) =>
                model.id == params.id
            );
        }

        // from server
        else {
            // attach влияет на indexOf,
            // выкидываем прикрепленные строки и пересчитываем indexOf
            if ( params._tempData.attach ) {
                for (let at in params._tempData.attach) {
                    let id = params._tempData.attach[ at ];

                    if ( id == params.id ) {
                        index = +at;
                        break;
                    }
                }
            }

            if ( index == -1 ) {
                if ( params._tempData.attach ) {
                    if ( !params.filter ) {
                        params.filter = new Filter();
                    }
                    params.filter.setCondition("__notIds", {
                        type: "in",
                        not: true,
                        key: "ID",
                        value: Object.values(params._tempData.attach)
                    });
                }

                this._addRequest("indexOf", params);

                params.row = {
                    ID: params.id
                };

                for (const sortItem of params.sort || []) {
                    const key = Object.keys(sortItem)
                        .filter((key) => key !== "NULLS")[0];

                    if ( key === "ID" ) {
                        continue;
                    }

                    const model = this.collection.get(params.id);
                    if ( model === undefined ) {
                        continue;
                    }
                    const value = model.get(key);

                    params.row[ key ] = value;
                }
                // если currency столбцы не умножать,
                // то будет в итоге ошибка
                params.row = this._normolizeFields(params.row);

                try {
                    index = await this.api.indexOf(params);
                    index = +index;
                } catch (_err) {
                    err = _err;
                }

                if ( params._tempData.attach ) {
                    for (let i in params._tempData.attach) {
                        if ( i < index ) {
                            index++;
                        }
                    }
                }

                // чтобы не нарушать порядок
                // они должны быть вызваны, после return response
                setTimeout(() => {
                    this._runParallels("indexOf", params, err, index);
                });
            }
        }

        if ( callback ) {
            return callback(err, index);
        }

        if ( err ) {
            throw err;
        }

        return index;
    }

    static generateID() {
        return ++id;
    }


    /**
     * Создание строки в таблице, используя TableApi.craete. Созданные строки
     * попадают в RowsCollection.
     * await create({}) => {}
     * await create() => {}
     * await create([]) => [{}]
     * @param {Object | Function} data данные, которые будут отправлены на создание строки
     * @param {Function} [callback]
     * @return {Promise<Backbone.Model>}
     */
    async create(nullOrRowOrRows, callback) {
        let rows;
        let isArrayResponse = false;

        if ( isFunction(nullOrRowOrRows) ) {
            callback = nullOrRowOrRows;
            nullOrRowOrRows = null;
        }

        if ( isObject(nullOrRowOrRows) ) {
            rows = [nullOrRowOrRows];
        }
        else if ( isArray(nullOrRowOrRows) ) {
            rows = nullOrRowOrRows;
            isArrayResponse = true;
        }
        else {
            rows = [{}];
        }

        // set default data by entity config
        rows = rows.map((row) => {
            let defData = {};
            this.columns.each((column) => {
                let value = column.get("default");

                // осторожно! значение по умолчанию в базе и null
                if ( column.get("safe") || value === undefined ) {
                    return;
                }

                if ( value ) {
                    defData[ column.get("key") ] = value;
                }
            });

            row = deepMixin(defData, row);

            return this._normolizeFields(row);
        });

        try {
            if ( !this._isLocal ) {
                rows = await this.api.create(rows);
            }
            else {
                rows = rows.map((row) => {
                    row.ID = row.id = RowsStorage.generateID();
                    return row;
                });
            }
        } catch (err) {
            if ( callback ) {
                return callback(err, null);
            } else {
                throw err;
            }
        }

        let err;
        let models = rows.map((row) => {
            let id = row.id;
            // событие по сокеты приходит раньше, чем вызывается этот callback
            let model = this.collection.get(id);
            this.___pauseUpdate = true;
            if ( !model ) {
                this.collection.add(row);
                model = this.collection.get(id);
            } else {
                model.set(row);
            }
            this.___pauseUpdate = false;

            if ( !model ) {
                err = new Error("error_creating_model");
                return;
            } else {
                this.sendEvent("create change", model);
            }

            return model;
        });

        // await create([]) => [{}]
        // await create({}) => {}
        // await create() => {}
        let response = models;
        if ( !isArrayResponse ) {
            response = models[0];
        }

        if ( callback ) {
            return callback(err, response);
        }

        if ( err ) {
            throw err;
        }

        return response;
    }

    /**
     * Перенос строк в корзину используя TableApi.trash. Удаленные строки удаляются из RowsCollection.
     * @param {Number | String | Array} id идентификатор модели или их массив
     * @param {Function} callback
     * callback(err, status)
     */
    async trash(id, callback) {
        let ids = [id];
        if ( isArray(id) ) {
            ids = id;
        }

        let status;
        try {
            if ( !this._isLocal ) {
                status = await this.api.trash(ids);
            }
            else {
                status = true;
            }
        } catch (err) {
            if ( callback ) {
                return callback(err);
            } else {
                throw err;
            }
        }

        // fix cache
        let models = this.collection.getModels(ids);
        if ( models && models.length ) {
            this.collection.remove(models);
        }

        // порядок не менять!
        // иначе событие remove у grid'a
        // на последней строке сработает не вовремя
        this.sendEvent("trash change", models);

        if ( callback ) {
            callback(null, status);
        } else {
            return status;
        }
    }

    /**
     * Полное удаление строк используя TableApi.remove. Удаленные строки удаляются из RowsCollection.
     * @param {Number | String | Array} id идентификатор модели или их массив
     * @param {Function} [callback]
     * callback(err, status)
     */
    async remove(id, callback) {
        let ids = [id];
        if ( isArray(id) ) {
            ids = id;
        }

        let status;
        try {
            status = await this.api.remove(ids);
        } catch (err) {
            if ( callback ) {
                return callback(err);
            } else {
                throw err;
            }
        }

        let models = this.collection.getModels(ids);
        if ( models && models.length ) {
            this.collection.remove(models);
        }

        // порядок не менять!
        // иначе событие remove у grid'a
        // на последней строке сработает сработает не вовремя
        this.sendEvent("remove change", models);

        if ( callback ) {
            callback(null, status);
        } else {
            return status;
        }
    }

    /**
     * Перенос строк из корзины используя TableApi.untrash. Удаленные строки добавляются в RowsCollection.
     * @param {Number | String | Array} id идентификатор модели или их массив
     * @param {Function} callback
     * callback(err, status)
     */
    async untrash(id, callback) {
        let ids = [id];
        if ( isArray(id) ) {
            ids = id;
        }

        //Проще было бы если у сокет клиента был бы id
        //но мы не ищем легки путей и на untrash делаем изменения по сокетам
        //чтоб не было лишних записей в кол-ве коллекции
        //делаем проверку по ид добавленным на этом клиенте последним untrash
        this._untrashed = {};
        ids.forEach((id) => {
            this._untrashed[id] = true;
        });

        let models = this.collection.getModels(ids);

        let status;
        try {
            status = await this.api.untrash(ids);
        } catch (err) {
            if ( callback ) {
                return callback(err);
            } else {
                throw err;
            }
        }

        if ( this.params.DELETED === 1 ) {
            this.collection.remove(models);
        }

        this.sendEvent("untrash change", models);

        if ( callback ) {
            callback(null, status);
        } else {
            return status;
        }
    }

    /**
     * Проверяем, можно ли выполнить .get запрос синхронно.
     * @return {Boolean} true, если возможно
     */
    canSyncGet() {
        if ( this.keep_in_memory || this._isLocal ) {
            return true;
        }

        return false;
    }

    /**
     * Изменяем данные строки
     * @param  {Number | String} id идентификатор строки, которую нужно обновить
     * @param  {Object} changes Объект с данными, которые изменились
     * @param  {Function} [callback] callback(err, rowModel)
     * @returns {Promise<Backbone.Model>}
     */
    async update(id, changes, callback) {
        let row;
        try {
            if ( this._isLocal ) {
                row = changes;
            } else {
                row = await this.api.update(
                    id,
                    this._normolizeFields(changes)
                );
            }
        } catch (err) {
            if ( callback ) {
                return callback(err, null);
            } else {
                throw err;
            }
        }

        // update может идти на строку, которой нету в кэше
        let model = this.collection.get(id);
        if ( !model ) {
            this.collection.add(row);
            model = this.collection.get(id);
        }

        // ___pauseUpdate - костыль,
        // чтобы остановить порочную связь collection change и api.update.
        // Изменение моделей слушают гриды
        this.___pauseUpdate = true;
        model.set(row);
        this.___pauseUpdate = false;

        setTimeout(() => {
            this.sendEvent("update change", model, changes);
        });

        if ( callback ) {
            callback(null, model);
        } else {
            return model;
        }
    }

    sendEvent() {
        let args = [].slice.call(arguments);

        // TODO: возможно это повлечет за собой лишние перезагрузки,
        // если они есть, их надо пофиксить

        // для удобства передаем сообщения на настоящую таблицу
        let storages = [
            RowsStorage.get(this.realName, 0), // usual
            RowsStorage.get(this.realName, 1) // trash
        ];

        if ( this.realName != this.name ) {
            storages.push(RowsStorage.get(this.name, 0)); // usual
            storages.push(RowsStorage.get(this.name, 1)); // trash
        }

        storages.forEach(function(storage) {
            storage.trigger.apply(storage, args);
            allStoragesEvents.trigger("change:" + storage.name);
        });
    }

    /**
     * преобразование дат в серверные
     * @param {Object} data Объект с данными, который надо преобразовать
     * @return {Object}
     */
    _normolizeFields(data) {
        data = deepClone(data);

        let column;
        let type;
        for (let key in data) {
            // Заплатка на использование дополнительных полей с андерскором
            // а вообще по хорошему нужно везде опции и fields для метода create разделить
            //TODO:
            if ( key.startsWith("_") ) {
                console.log("is system option: " + key + ", skip");
                continue;
            }

            column = this.columns.getByKey(key);
            if ( !column ) {
                if ( key != "COLORS" ) {
                    console.error("column is undefined: " + this.name + "." + key);
                }
                continue;
            }
            type = column.get("type");

            let value = data[key];

            if ( type == "date" || type == "datetime" ) {
                if ( type == "date" && value != null ) {
                    // LW-3293
                    // Добавление 13 часов необходимо из-за бага с фильтром колонок.
                    // Проблема заключается в переходе на летнее время,
                    // поскольку в вразные временные даты значение местного времени относительно,
                    // utc(0) может разнится на один час
                    // в фильтре может отображаться,к примеру 29 число, вместо 30
                    // поскольку getUniqueDaysAsync не учитывает такие изменения.

                    // LW-3756
                    // если value приходит ввиде cтроки, то это значит что нужно обнулить время,
                    // что бы даты не перескакивали при отображении в гридах
                    if ( typeof value == "string" ) {
                        // Проверяем, что это ISO
                        // 2019-02-13T21:00:00.000Z
                        // т.к. из буфера обмена приходит:
                        // 13.10.2019
                        if ( /^\d\d\d\d-\d\d-\d\d/.test(value) ) {
                            value = value.substring(0, 11) + "00:00:00" + value.substring(19);
                            value = +parseDate(value).date;
                        }
                    }

                    value = clientTime2serverTime(value);
                    value = parseDate(value).date.setHours(0) + 13 * 60 * 60 * 1000;
                }

                data[key] = clientTime2serverTime(value);
            }

            if ( type == "json" ) {
                try {
                    data[key] = JSON.stringify(value);
                } catch (e) {
                    console.log(e);
                }
            }

            if ( type == "currency" ) {
                if ( value == null || value === "" || !/^[\d\s.-]+$/.test(value + "") ) {
                    data[key] = null;
                } else {
                // "1 200.00" => 1200
                    value = currencyToNumber(value);
                    // 42606.59 * 10000 = 426065899.99999994
                    data[key] = round2(value * 10000, 0); // TAG: currency10000
                }
            }

            // lw-11291 при копипасте копируются текстовые значения полей, а в бд для чекбоксов используется smallint
            if ( type == "checkbox" && typeof value == "string" ) {
                if ( value.toLowerCase() == langDictionary.get("Yes").toLowerCase() ) {
                    data[key] = 1;
                }
                else if ( value.toLowerCase() == langDictionary.get("No").toLowerCase() ) {
                    data[key] = 0;
                }
            }
        }

        return data;
    }

    /**
     * Изменяем сразу несколько строк
     * @param {Array} rows массив объектов,
     * где каждый объект это изменения,
     * в каждом объекте должно быть поле id
     * @returns {Promise<Backbone.Model[]>}
     */
    async updateRows(rows, callback) {
        try {
            let models = [];
            for (let i = 0, n = rows.length; i < n; i++) {
                let row = rows[ i ];
                row = deepClone(row);

                let id = row.id;
                delete row.id;

                let model = await this.update(id, row);
                models.push(model);
            }

            if ( callback ) {
                callback(null, models);
            } else {
                return models;
            }
        } catch (err) {
            if ( callback ) {
                callback(err);
            } else {
                throw err;
            }
        }
    }

    /**
     * Создаем сразу несколько строк
     * @param {Array} rows массив объектов,
     * @param {Function} [callback]
     * где каждый объект это данные строки
     * @returns {Promise<Backbone.Model[]>}
     */
    async createRows(rows, callback) {
        rows = deepClone(rows);

        try {
            let models = [];
            for (let i = 0, n = rows.length; i < n; i++) {
                let row = rows[ i ];
                let model = await this.create(row);
                models.push(model);
            }

            if ( callback ) {
                callback(null, models);
            } else {
                return models;
            }
        } catch (err) {
            if ( callback ) {
                callback(err);
            } else {
                throw err;
            }
        }
    }

    initCollection() {
        /**
         * @member {RowsCollection}
         */
        this.collection = new RowsCollection([], { storage: this });
        this.collection.comparator = "id";

        this.collection.on("change", function(model, options) {
            let isCollection = model._byId;
            if ( isCollection ) {
                return;
            }

            // see RowsStorage.prototype.get
            if ( this.___pauseUpdate || options.update === false ) {
                return;
            }

            let id = model.get("id");
            let changes = deepClone(model.changed);

            // изменение одного поля может повлечь за собой изменение других полей (на стороне сервера)
            this.api.update(id, this._normolizeFields(changes), function(err, row) {
                this.___pauseUpdate = true;
                model.set(row);
                this.___pauseUpdate = false;
            }.bind(this));
        }.bind(this));
    }

    /**
     * Выполняем запрос напрямую, без кэша, а потом дополняем кэш результатом
     * @param  {GetParams} params Get параметры запроса
     * @param  {Function}  callback
     * @returns {Promise<Backbone.Model[]>}
     */
    async refreshGet(params, callback) {
        if ( callback ) {
            this.get(params, callback, true);
        } else {
            return await this.get(params, callback, true);
        }
    }

    initSocket() {
        let onSocketEvent = function(type, data) {
            if ( type == "update" ) {
                this.onSocketUpdate(data);
            }
            else if ( type == "create" ) {
                this.onSocketCreate(data);
            }
            else if ( type == "remove" ) {
                this.onSocketRemove(data);
            }
            else {
                let models;
                let rows;
                rows = data;

                if ( !isArray(rows) || !rows.length ) {
                    return;
                }

                // по сокету приходят в нижнем регистре
                rows.map(toUpperFields);

                if ( this.params.DELETED == 1 ) {
                    if ( type == "trash" ) {
                        rows.map(this._addRowToCache.bind(this));
                        models = this.collection.getModels(rows);
                        this.sendEvent("trash change", models);
                    }
                    else if ( type == "untrash" ) {
                        models = this._removeRowsFromCache(rows.map(getId));

                        if ( models.length ) {
                            this.sendEvent("untrash change", models);
                        }
                    }
                }
                else if ( type == "trash" ) {
                    models = this._removeRowsFromCache(rows.map(getId));

                    if ( models.length ) {
                        this.sendEvent("trash change", models);
                    }
                }
                else if ( type == "untrash" ) {
                    rows.map(this._addRowToCache.bind(this));
                    models = this.collection.getModels(rows);
                    this.sendEvent("untrash change", models);
                }
            }
        }.bind(this);

        if ( window.system ) {
            window.system.socketClient.subscribeOnTable(this.name, onSocketEvent);
        }
    }

    onSocketUpdate(rows) {
        if ( isObject(rows) ) {
            rows = [rows];
        }

        let ids = [];
        let rowById = {};
        rows = rows.filter(function(row) {
            let id = getId(row);
            row = toUpperFields(row);
            row = this._processRow(row); // TAG: currency10000

            if ( (+row.DELETED || 0) != (+this.params.DELETED || 0) ) {
                return;
            }

            delete row.id;
            delete row.ID;
            delete row._id;

            let model = this.collection.get(id);
            let hasChanges = false;
            if ( model ) {
                for (let key in row) {
                    if ( row[key] != model.attributes[key] ) {
                        hasChanges = true;
                        break;
                    }
                }
            } else {
                hasChanges = true;
            }
            setId(row, id);

            if ( !hasChanges ) {
                return;
            }

            if ( Object.keys(row).length == 1
                && (
                    row.COLORS || row.colors
                    || row.id_list_user_update || row.ID_LIST_USER_UPDATE
                    || row.dt_update || row.DT_UPDATE
                )
            ) {
                this.___pauseUpdate = true;
                this.collection.add([row], { merge: true });
                this.___pauseUpdate = false;
                return;
            }

            ids.push(id);
            rowById[ id ] = row;
        }, this);

        if ( !ids.length ) {
            return;
        }

        // no refreshGet !!!
        this.___pauseUpdate = true;
        ids.forEach(function(id) {
            let row = rowById[ id ];

            this.collection.add([row], { merge: true });
        }, this);
        this.___pauseUpdate = false;
    }

    // only for local storage
    addLocalRows(rows) {
        if ( !this._isLocal ) {
            throw new Error("only for local storage");
        }

        rows.forEach((row) =>
            this._addRowToCache(row)
        );
    }

    initLangChanges() {
        if ( !window.system ) {
            return;
        }

        this.listenTo(window.system, "lang:fields:change", (event) => {
            if ( event.entity !== this.name ) {
                return;
            }

            const tableScheme = scheme[ this.name ];
            if ( tableScheme ) {
                for (const schemeColumn of tableScheme.columns) {
                    const key = schemeColumn.key;
                    const newTitle = langDictionary.getFieldTitle(this.name, key);

                    const column = this.columns.getByKey(key);

                    schemeColumn.title = newTitle;
                    column.set("title", newTitle);
                }
            }
        });
    }

    reset() {
        this.collection.reset();
    }

    _removeRowsFromCache(ids) {
        let models = [];
        ids.forEach(function(id) {
            let model = this.collection.get(id);

            if ( !model ) {
                return;
            }

            this.collection.remove(model);

            models.push(model);
        }, this);

        return models;
    }

    onSocketRemove(rows) {
        let ids = rows.map(function(row) {
            if ( isObject(row) ) {
                return getId(row);
            } else {
                return row;
            }
        });

        let models = this._removeRowsFromCache(ids);

        if ( models.length ) {
            this.sendEvent("remove change", models);
        }
    }

    onSocketCreate(row) {
        row = this._processRow(row);// TAG: currency10000
        let model = this._addRowToCache(row);
        this.sendEvent("socketCreate", model);
    }

    addRowToCache(row) {
        return this._addRowToCache(row);
    }

    _addRowToCache(row) {
        let id = getId(row);

        //однодневный костыль, надо исправить баг, а там презентация и сервер ронять нельзя(
        if ( !row.id ) {
            row.id = row._id || row.ID;
        }

        let model = this.collection.get(id);

        if ( !model ) {
            this.collection.add(row);
            model = this.collection.get(id);
        } else {
            model.set(row, { silent: true });
        }

        return model;
    }

    _resetAndUpdateCacheRows(rows) {
        let newRowById = {};
        rows.forEach((row) => {
            let id = getId(row);
            //не костыль, а фича
            if ( !row.id ) {
                row.id = row._id || row.ID;
            }

            newRowById[ id ] = row;

            this.___pauseUpdate = true;
            this.collection.add([row], { merge: true });
            this.___pauseUpdate = false;
        });

        let toRemove = [];
        this.collection.each((model) => {
            if ( model.id in newRowById ) {
                return;
            }

            toRemove.push(model);
        });

        this.collection.remove(toRemove);
    }

    async lock(id, callback) {
        let result;
        let err;

        if ( this._isLocal ) {
            result = { state: false };
        } else {
            try {
                result = await this.api.lock(id);
            } catch (_err) {
                err = _err;
            }
        }

        if ( callback ) {
            callback(err, result);
        } else {
            if ( err ) {
                throw err;
            }

            return result;
        }
    }

    async unlock(id, callback) {
        let result;
        let err;

        if ( this._isLocal ) {
            result = { state: false };
        } else {
            try {
                result = await this.api.unlock(id);
            } catch (_err) {
                err = _err;
            }
        }

        if ( callback ) {
            callback(err, result);
        } else {
            if ( err ) {
                throw err;
            }

            return result;
        }
    }

    /**
     * для компонента фильтров по столбцу с типом дата необходимо
     * знать список уникальных дат
     * @param {Object} getParams такие же параметры как у get, но с добавлением
     * одного поля, {String} getParams.field, поля по которому получаем
     * список уникальных дат
     * @param {Function} callback
     */
    async getUniqueDays(getParams, callback) {
        getParams = deepClone(getParams);
        getParams.DELETED = this.params.DELETED;

        let result;
        let err;
        try {
            if ( this._isLocal ) {
                result = this.collection.getUniqueDays(getParams);
            } else {
                result = await this.api.getUniqueDays(getParams);
            }
        } catch (_err) {
            err = _err;
        }

        if ( callback ) {
            callback(err, result);
        } else {
            if ( err ) {
                throw err;
            }

            return result;
        }
    }

    /**
     * Обновляем то, что есть в кэше,
     * сообщаем всем слушателям, что в базе что-то поменялось
     * и их запросы надо обновить напрямую из базы
     * callback(err)
     */
    reload() {
        // иногда storage выступает в роли прокcи шины событий
        // ChangeOrderTask
        // timeout нужен, чтобы не забивать очередь
        setTimeout(() => {
            // если грид виден, то его нужно перезагрузить
            this.trigger("liveReload");
        }, 300);

        return this;
    }

    initUpdateLinkTables() {
        let linkTables = this.joinTables || [];

        if ( config[this.realName] && config[this.realName].linkTables ) {
            linkTables = linkTables.concat(config[this.realName].linkTables);
        }

        if ( config[this.name] && config[this.name].linkTables ) {
            linkTables = linkTables.concat(config[this.name].linkTables);
        }

        linkTables = removeRepeat(linkTables);
        linkTables = linkTables.map((linkTable) => linkTable.toUpperCase());

        linkTables.forEach((table) => {
            //update create trash untrash remove
            // change можно использовать и для кастомных изменений,
            // например привязка юнита к заказу происходит одним запросом,
            // но затрагивает сразу несколько таблиц,
            // в некоторых из них произошел update, а где-то insert
            this.listenTo(
                allStoragesEvents,
                "change:" + table,
                this.reload.bind(this)
            );
        });

        this.linkTables = linkTables;
    }

    clear() {
    }

    /**
     * Создание локальной таблицы (только в памяти браузера)
     * @param {string} name - имя таблицы для static метода RowsStorage.get(name)
     * @param {Column[]} columns - список столбцов {type, key, title}
     * @param {object[]} rows - список строк
     * @returns {RowsStorage}
     */
    static createLocal(name, columns, rows) {
        let storage = RowsStorage.get(name);
        storage.columns.add(columns);
        storage.collection.add(rows.map(function(row) {
            row = deepClone(row);
            setId(row, getId(row));
            return row;
        }));
        storage._isLocal = true;
        return storage;
    }

    /**
     * Создание локальной таблицы (только в памяти браузера) с рандомным названием
     * @param {Column[]} columns - список столбцов {type, key, title}
     * @param {object[]} rows - список строк
     * @returns {RowsStorage}
     */
    static createTmpLocal(columns, rows = []) {
        const randomStorageName = "local-storage-" + (++localStorageId);
        return RowsStorage.createLocal(
            randomStorageName,
            columns,
            rows
        );
    }

    async export(params, callback) {
        params = this._prepareGetParams("export", params);
        return await this.api.export(params, callback);
    }
}

function extractUserId() {
    if ( window.system && window.system.user ) {
        return window.system.user.id;
    }

    try {
        const user = JSON.parse($.cookie("user"));
        return +user.id || null;
    } catch (err) {
        console.error(err);
    }
}
RowsStorage._scheme = scheme; // for deubg

function isDeadJoin(column) {
    const {requiredJoins, isOnlyColumn} = column.attributes;

    if ( !isOnlyColumn || !requiredJoins.length ) {
        return false;
    }

    const hasDeadJoin = requiredJoins.some((realTable) =>
        realTable in deadTables
    );
    return hasDeadJoin;
}

function getDeadJoinEntityName(column) {
    const {requiredJoins} = column.attributes;
    const deadJoinTable = requiredJoins.find((realTable) =>
        realTable in deadTables
    );
    const deadEntityName = deadTables[ deadJoinTable ];
    return deadEntityName;
}

RowsStorage.storages = storages;


// for debug
window.RowsStorage = RowsStorage;
